<template>
    <v-text-field ref="vTextFieldRef" :model-value="displayValue" :clearable="clearable" :placeholder="placeholder" :hint="hint" :type="type" :label="label" :min="min" :error-messages="transformError((form?.errors as Record<string, string> | undefined)?.[String(field)])" @update:model-value="updateFieldValue" @update:focused="(focused: boolean) => { if (!focused) { form?.touch(field as string)?.validate() } }" />
</template>

<script setup lang="ts" generic="T extends InertiaFormData">
import { useUtility } from '@shared/composables/useUtility';
import type { InertiaFormData, PrecognitionForm } from '@shared/types/Form';
import { onBeforeUnmount, onMounted, useTemplateRef } from 'vue';

type DisplayFormatter = (value: unknown) => unknown;

interface Props {
    field: string;
    label?: string | null;
    type?: 'text' | 'number';
    hint?: string | null;
    placeholder?: string | null;
    clearable?: boolean;
    min?: number | string | null;
    displayFormatter?: DisplayFormatter | null;
}

const { transformError } = useUtility();

const vTextFieldRef = useTemplateRef<{ $el: HTMLElement } | null>('vTextFieldRef');
let boundEl: HTMLElement | null = null;

onMounted(() => {
    const root = vTextFieldRef.value?.$el ?? null;
    if (!root) return;
    boundEl = root;
    boundEl.addEventListener('keydown', onKeyDown as EventListener, { capture: true });
    boundEl.addEventListener('paste', onPaste as EventListener, { capture: true });
});

onBeforeUnmount(() => {
    if (!boundEl) return;
    boundEl.removeEventListener('keydown', onKeyDown as EventListener, { capture: true } as EventListenerOptions);
    boundEl.removeEventListener('paste', onPaste as EventListener, { capture: true } as EventListenerOptions);
    boundEl = null;
});

const props = withDefaults(defineProps<Props>(), {
    label: null,
    type: 'text',
    hint: null,
    placeholder: null,
    clearable: false,
    min: null,
    displayFormatter: null,
});

const minValue = computed<number | null>(() => {
    if (props.min === null || props.min === '') return null;
    const n = Number(props.min);
    return Number.isFinite(n) ? n : null;
});

function isIndexable(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function getNestedValue(source: unknown, path: string[]): unknown {
    let target: unknown = source;

    for (const key of path) {
        if (!isIndexable(target)) {
            return target;
        }

        target = target[key];
    }

    return target;
}

function setNestedValue(source: unknown, path: string[], value: unknown): void {
    const last = path.pop();

    if (!last) {
        return;
    }

    let target: unknown = source;

    for (const key of path) {
        if (!isIndexable(target)) {
            return;
        }

        target = target[key];
    }

    if (isIndexable(target)) {
        target[last] = value;
    }
}

function onKeyDown(e: KeyboardEvent) {
    if (props.type !== 'number') return;
    if (minValue.value !== null && minValue.value >= 0 && (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+')) {
        e.preventDefault();
    }
}

function onPaste(e: ClipboardEvent) {
    if (props.type !== 'number') return;
    if (minValue.value === null || minValue.value < 0) return;
    const text = e.clipboardData?.getData('text') ?? '';
    const num = Number(text);
    if (Number.isFinite(num) && num < minValue.value) {
        e.preventDefault();
    }
}

const form = defineModel<PrecognitionForm<T>>('form', {
    required: true,
});

function updateFieldValue(newValue: unknown) {
    fieldValue.value = newValue;
}

const fieldValue = computed<unknown>({
    get(): unknown {
        return getNestedValue(form.value, String(props.field).split('.'));
    },
    set(newValue: unknown): void {
        let value: unknown = newValue;
        if (props.type === 'number' && minValue.value !== null) {
            const num = Number(value);
            if (Number.isFinite(num) && num < minValue.value) {
                value = minValue.value;
            }
        }

        setNestedValue(form.value, String(props.field).split('.'), value);
    },
});

const displayValue = computed<unknown>(() => {
    const value: unknown = fieldValue.value;

    return typeof props.displayFormatter === 'function' ? props.displayFormatter(value) : value;
});
</script>
